import React, { useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import { useHistory } from 'react-router-dom';
import { createNewBusiness } from '../../../api/admin/businesses/businesses';
import passwordValidation from '../../../utils/passwordValidation';
import CentsLoader from '../../common/centsLoader/CentsLoader';

const CreateNewBusiness = () => {
  const [loading, setLoading] = useState(false);
  const [newBusiness, setNewBusiness] = useState({
    name: null,
    address: null,
    city: null,
    state: null,
    zipCode: null,
    salesforceId: null,
  });
  const [newUser, setNewUser] = useState({
    firstName: null,
    lastName: null,
    email: null,
    phone: null,
    password: null,
  });
  const [error, setError] = useState();
  const [isVerifiedNotInLW, setIsVerifiedNotInLW] = useState(false);
  let history = useHistory();

  /**
   * Store the incoming input value into our newBusiness state object
   *
   * @param {String} value
   * @param {String} field
   */
  const handleInputChange = (value, field, type) => {
    setError(null);

    if (type === 'business') {
      setNewBusiness({
        ...newBusiness,
        [field]: value,
      });
    }

    if (type === 'user') {
      setNewUser({
        ...newUser,
        [field]: value,
      });
    }
  };

  /**
   * Return a boolean determining whether the business section is complete
   */
  const businessFormNotFinished = () => {
    return (
      !newBusiness.name ||
      !newBusiness.address ||
      !newBusiness.city ||
      !newBusiness.state ||
      !newBusiness.zipCode
    );
  };

  /**
   * Return a boolean determining whether the business section is complete
   */
  const businessOwnerFormNotFinished = () => {
    return (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.phone ||
      !newUser.password
    );
  };

  /**
   * Disable the submit button based on certain criteria missing
   */
  const disableButton = () => {
    return businessFormNotFinished() || businessOwnerFormNotFinished() || !isVerifiedNotInLW;
  };

  /**
   * Store the user and business credentials in our database
   */
  const saveNewBusiness = async () => {
    if (disableButton()) {
      return;
    }

    const userPasswordValidation = passwordValidation(newUser?.password);

    if (!userPasswordValidation.result) {
      setLoading(false);
      setError(userPasswordValidation.errorMessage);
      return;
    }

    try {
      setLoading(true);

      const data = {
        user: newUser,
        business: newBusiness,
      };

      await createNewBusiness(data);

      setLoading(false);

      navigateToBusinessTable();
    } catch (error) {
      setLoading(false);
      setError(error.response.data.error);
    }
  };

  /**
   * Navigate to the list of Cents businesses
   */
  const navigateToBusinessTable = () => {
    history.push('/businesses');
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading && (
        <div className="create-new-user-container">
          <div className="create-new-user-form-container">
            <div className="create-new-user-form">
              <h1>Business Information</h1>
              <div className="create-new-user-form-row">
                <p className="label">Name</p>
                <TextField
                  variant="outlined"
                  label="Name"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'name', 'business');
                  }}
                  value={newBusiness.name}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Address</p>
                <TextField
                  variant="outlined"
                  label="Address"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'address', 'business');
                  }}
                  value={newBusiness.address}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">City</p>
                <TextField
                  variant="outlined"
                  label="City"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'city', 'business');
                  }}
                  value={newBusiness.city}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">State</p>
                <TextField
                  variant="outlined"
                  label="State"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'state', 'business');
                  }}
                  value={newBusiness.state}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Zip Code</p>
                <TextField
                  variant="outlined"
                  label="Zip Code"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'zipCode', 'business');
                  }}
                  value={newBusiness.zipCode}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Salesforce ID</p>
                <TextField
                  variant="outlined"
                  label="Salesforce ID"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'salesforceId', 'business');
                  }}
                  value={newBusiness.salesforceId}
                />
              </div>
              <h1 style={{ paddingTop: '20px' }}>Business Owner Information</h1>
              <div className="create-new-user-form-row">
                <p className="label">First Name</p>
                <TextField
                  variant="outlined"
                  label="First Name"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'firstName', 'user');
                  }}
                  value={newUser.firstName}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Last Name</p>
                <TextField
                  variant="outlined"
                  label="Last Name"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'lastName', 'user');
                  }}
                  value={newUser.lastName}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Email</p>
                <TextField
                  variant="outlined"
                  label="Email"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'email', 'user');
                  }}
                  value={newUser.email}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Phone Number</p>
                <TextField
                  variant="outlined"
                  label="Phone Number"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'phone', 'user');
                  }}
                  value={newUser.phone}
                />
              </div>
              <div className="create-new-user-form-row">
                <p className="label">Password</p>
                <TextField
                  variant="outlined"
                  label="Password"
                  onChange={(event) => {
                    handleInputChange(event.target.value, 'password', 'user');
                  }}
                  type="password"
                  value={newUser.password}
                />
              </div>
              <div className="create-new-user-form-row">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isVerifiedNotInLW}
                      onChange={(event) => {
                        setIsVerifiedNotInLW(event.target.checked);
                      }}
                    />
                  }
                  label="I have verified this does not exist in Laundroworks"
                />
              </div>
              <div className="submit-button-container">
                <button
                  className={'submit-button ' + (disableButton() ? 'disabled' : 'active')}
                  disabled={disableButton()}
                  onClick={() => {
                    saveNewBusiness();
                  }}
                >
                  Create
                </button>
                {error && <p className="error-text">{error}</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateNewBusiness;
